<template>
    <div>
        <div class="form-input-select--label">
            <span v-if="label" class="form-input-select__placeholder">{{ label }}:</span>

            <span v-if="showInfoTip">
                <common-info-tip>
                    <span>{{ infoTipMessage }}</span>
                </common-info-tip>
            </span>
        </div>
        <div v-click-away="close" :class="inputClass">
            <select
                ref="select"
                :value="prefill"
                :autofocus="hasAutofocus"
                :disabled="isDisabled"
                :required="isRequired"
                @keydown.up.prevent="handleUpArrow()"
                @keydown.down.prevent="handleDownArrow()"
                @keydown.enter.prevent="handleEnter()"
                @keydown.esc.prevent="close()"
                @keydown.space.prevent="toggle()"
                @click.prevent="toggle()"
                @blur="blur($event)"
                @focus="focus()"
                @change="select($event)"
            >
                <option v-if="isNullable"></option>
                <option v-for="(option, index) in optionsFiltered" :key="index" :value="option.value">
                    {{ option.label }}
                </option>
            </select>

            <div :class="wrapperClass" @click.stop>
                <div :class="valueClass" @click="click()">
                    <div class="form-input-select__icon-label">
                        <common-icon v-if="icon" :name="icon" size="large"></common-icon>
                        <span v-if="!isSearchable" class="form-input-select__label">{{ inputLabel }}</span>
                        <input
                            v-if="isSearchable"
                            ref="search"
                            v-model="searchTerm"
                            class="form-input-select__search-box"
                            type="text"
                            :disabled="isDisabled"
                            :placeholder="placeholder"
                            @input="searchTermChanged()"
                        />
                    </div>
                    <common-icon v-if="!isSearchable" name="dropdown-arrow" size="medium"></common-icon>
                </div>

                <div ref="options" :class="optionsClass" @mouseleave="hoverOption(-1)">
                    <common-grid-items v-if="showGrid">
                        <button
                            v-if="isNullable"
                            :class="classOption(undefined, optionsFiltered?.length)"
                            type="button"
                            tabindex="-1"
                            @mouseover="hoverOption(optionsFiltered?.length)"
                            @click="selectNullable()"
                        >
                            <span class="form-input-select__options--none">{{ $t('common.microcopy.none') }}</span>
                        </button>
                        <button
                            v-for="(option, index) in optionsFiltered"
                            :key="index"
                            :class="classOption(option, index)"
                            type="button"
                            tabindex="-1"
                            @mouseover="hoverOption(index)"
                            @click="select(option.value)"
                        >
                            <span :title="option.label">{{ option.label }}</span>
                            <common-icon v-if="option.value === prefill" name="dropdown-check" size="small"></common-icon>
                        </button>
                    </common-grid-items>
                    <div v-else>
                        <button
                            v-if="isNullable"
                            :class="classOption(undefined, optionsFiltered?.length)"
                            type="button"
                            tabindex="-1"
                            @mouseover="hoverOption(optionsFiltered?.length)"
                            @click="selectNullable()"
                        >
                            <span class="form-input-select__options--none">{{ $t('common.microcopy.none') }}</span>
                        </button>
                        <button
                            v-for="(option, index) in optionsFiltered"
                            :key="index"
                            ref="option"
                            :class="classOption(option, index)"
                            type="button"
                            tabindex="-1"
                            @mouseover="hoverOption(index)"
                            @click="select(option.value)"
                        >
                            <span :title="option.label">{{ option.label }}</span>
                            <common-icon v-if="option.value === prefill" name="dropdown-check" size="small"></common-icon>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <span v-if="errorMessage" class="form-input-text--error">{{ errorMessage }}</span>
    </div>
</template>

<script>
    import FormInputMixin from 'mixins/FormInputMixin'
    import IndicateScrollMixin from 'mixins/IndicateScrollMixin'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'

    export default {
        mixins: [FormInputMixin, IndicateScrollMixin, WindowResizeMixin],

        props: {
            options: { type: Array, required: true },
            isNullable: { type: Boolean, default: false },
            largeDropdown: { type: Boolean, default: false },
            icon: { type: String, default: undefined },
            isSearchable: { type: Boolean, default: false },
            showInfoTip: { type: Boolean, default: false },
            infoTipMessage: { type: String, default: '' },
        },

        data() {
            return {
                isOpen: false,
                isFocused: false,
                highlightedOption: null,
                searchTerm: '',
                showAllOptions: true,
                isContentInViewport: false,
            }
        },

        computed: {
            inputClass() {
                return {
                    'form-input-select': true,
                    'form-input-select--is-open': this.isOpen,
                    'form-input-select--is-focused': this.isFocused && !this.isSearchable,
                    'form-input-select--is-searchable': this.isSearchable && this.isOpen,
                    'form-input-select--is-invalid': this.errorMessage,
                    'form-input-select--is-disabled': this.isDisabled,
                }
            },

            valueClass() {
                return {
                    'form-input-select__input': true,
                    'form-input-select__input--searchable': this.isSearchable,
                    'form-input-select__input--has-no-value': !this.prefill,
                }
            },

            wrapperClass() {
                return {
                    'form-input-select__wrapper': true,
                    'form-input-select__wrapper--grid': this.showGrid,
                }
            },

            optionsClass() {
                return {
                    'form-input-select__options': true,
                    'form-input-select__options--grid': this.showGrid,
                    'form-input-select__options--grid-left': this.showGrid,
                }
            },

            showGrid() {
                this.isInViewport()
                return this.largeDropdown && this.options?.length > 5 && !this.isSmartphoneLarge
            },

            inputLabel() {
                return this.prefill ? this.selectedOption.label : this.placeholder
            },

            selectedOption() {
                return this.selectedIndex > -1 ? this.options[this.selectedIndex] : {}
            },

            selectedIndex() {
                return this.options.findIndex(({ value }) => JSON.stringify(this.prefill) == JSON.stringify(value))
            },

            hasOptions() {
                return this.$refs.option && this.$refs.option?.length
            },

            optionsFiltered() {
                if (this.showAllOptions) {
                    return this.options
                } else {
                    return this.options.filter((option) =>
                        option.label
                            .toString()
                            .toLowerCase()
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '')
                            .includes(
                                this.searchTerm
                                    ?.trim()
                                    .toLowerCase()
                                    .normalize('NFD')
                                    .replace(/[\u0300-\u036f]/g, ''),
                            ),
                    )
                }
            },
        },

        methods: {
            classOption(option, optionIndex, isOpen = false) {
                return {
                    'form-input-select__option': true,
                    'form-input-select__option--is-highlighted': optionIndex === this.highlightedOption,
                    'form-input-select__suboption': isOpen,
                    'form-input-select__selected-option': option?.value === this.prefill,
                }
            },

            hoverOption(index) {
                this.highlightedOption = index
            },

            scrollToHighlightedOption() {
                const elements = this.$el.getElementsByClassName('form-input-select__option')
                const selectedIndex = Array.from(elements).findIndex((el) =>
                    el.classList.contains('form-input-select__selected-option'),
                )
                if (elements && elements.length) {
                    let scrollToElementIndex = 4
                    if (selectedIndex > scrollToElementIndex) {
                        scrollToElementIndex = selectedIndex
                    } else if (elements.length <= scrollToElementIndex) {
                        scrollToElementIndex = elements.length - 1
                    }
                    elements[scrollToElementIndex].scrollIntoView({ behavior: 'instant', block: 'nearest' })
                }
            },

            switchHighlightedOption(direction = 1) {
                const position = this.highlightedOption + direction

                if (position < 0) {
                    this.highlightedOption = this.options?.length - 1
                } else {
                    this.highlightedOption = position % this.options?.length
                }
                this.scrollToHighlightedOption()
            },

            blur(event) {
                if (event.relatedTarget && !this.isDescendant(event.relatedTarget, this.$el)) {
                    this.close()
                }
            },

            focus() {
                this.isInViewport()

                this.isFocused = true
                this.highlightedOption = this.selectedIndex
                this.scrollToHighlightedOption()

                this.$refs.select.focus()
            },

            click() {
                this.isInViewport()
                if (!this.isSearchable) {
                    this.$refs.select.focus()
                } else {
                    this.$refs.search.focus()
                }
                this.$refs.select.click()
                this.scrollToHighlightedOption()
            },

            close() {
                this.isOpen = false
                this.isFocused = false
            },

            toggle() {
                this.showAllOptions = true
                this.isOpen = !this.isOpen
            },

            select(option) {
                this.close()
                this.change(option)
                this.searchTerm = this.inputLabel
                this.$refs.select.focus()
            },

            change(event) {
                let value = event instanceof Event ? event.target.value : event
                if (value !== undefined) {
                    this.emitEvent({
                        name: 'change',
                        payload: { path: this.valuePath, value: Number(value) || value },
                    })
                }
            },

            searchTermChanged() {
                this.clearInput()
                if (!this.isSearchable) {
                    this.$refs.select.focus()
                } else {
                    this.$refs.search.focus()
                }

                this.showAllOptions = false

                if (!this.isOpen) {
                    this.isOpen = true
                }
                this.emitEvent({ name: 'search-term-changed', payload: { searchTerm: this.searchTerm, index: this.valuePath } })
            },

            selectNullable() {
                this.close()
                this.clearInput()
                this.searchTerm = ''
                this.$refs.select.focus()
            },

            handleUpArrow() {
                if (this.isOpen) {
                    this.switchHighlightedOption(-1)
                } else {
                    this.toggle()
                }
            },

            handleDownArrow() {
                if (this.isOpen) {
                    this.switchHighlightedOption(1)
                } else {
                    this.toggle()
                }
            },

            handleEnter() {
                if (this.isOpen && this.hasOptions) {
                    this.select(this.options[this.highlightedOption].value)
                } else {
                    this.toggle()
                }
            },

            isInViewport() {
                const elContent = this.$refs.options
                if (elContent) {
                    const bounding = elContent?.getBoundingClientRect()
                    this.isContentInViewport =
                        bounding.top >= 0 &&
                        bounding.left >= 0 &&
                        bounding.right <= window.innerWidth &&
                        bounding.bottom <= window.innerHeight
                }
            },

            windowResize() {
                this.setWindowWidth()
                this.isInViewport()
            },
        },

        watch: {
            prefill() {
                this.searchTerm = this.selectedOption.label || this.searchTerm
                this.highlightedOption = this.selectedIndex || null
            },
        },

        mounted() {
            this.$nextTick(() => {
                this.searchTerm = this.selectedOption.label || this.searchTerm
            })
            this.highlightedOption = this.selectedIndex || null
        },
    }
</script>
